/**
 * Deadline Tracker Page V2
 * Gantt-style timeline view for deadlines with visual progress tracking
 * Uses v2 API endpoints
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Calendar,
  Clock, 
  AlertTriangle,
  Filter,
  Loader2,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Flag,
  Target,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';

import ObligationDetailDrawerV2 from '../../components/obligations/ObligationDetailDrawerV2';
import { apiFetch } from '../../services/api_service';

export default function DeadlineTrackerPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deadlines, setDeadlines] = useState([]);
  const [summary, setSummary] = useState({});
  const [viewMode, setViewMode] = useState('timeline'); // 'timeline', 'calendar', 'list'
  const [selectedObligation, setSelectedObligation] = useState(null);
  const [timelineStart, setTimelineStart] = useState(new Date());

  const fetchDeadlines = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch('/v1/obligations-v2/analytics/deadlines');
      if (data?._error) throw new Error('Failed to fetch deadlines');
      setDeadlines(data.deadlines || []);
      setSummary(data.summary || {});
    } catch (err) {
      console.error('Error fetching deadlines:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDeadlines();
  }, [fetchDeadlines]);

  // Generate timeline days
  const timelineDays = useMemo(() => {
    const days = [];
    const start = new Date(timelineStart);
    start.setHours(0, 0, 0, 0);
    
    for (let i = -7; i < 35; i++) {
      const date = new Date(start);
      date.setDate(date.getDate() + i);
      days.push(date);
    }
    return days;
  }, [timelineStart]);

  const shiftTimeline = (days) => {
    const newStart = new Date(timelineStart);
    newStart.setDate(newStart.getDate() + days);
    setTimelineStart(newStart);
  };

  const goToToday = () => {
    setTimelineStart(new Date());
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getDayName = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const getDeadlinesForDay = (date) => {
    return deadlines.filter(d => {
      const dueDate = new Date(d.due_date);
      return dueDate.toDateString() === date.toDateString();
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="deadline-tracker-loading">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
        <span className="ml-2 text-[#6B6B6B]">Loading deadline tracker...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
        <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-3" />
        <p className="text-red-400 mb-4">{error}</p>
        <button onClick={fetchDeadlines} className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="deadline-tracker-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Deadline Tracker</h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            Visual timeline of all obligation deadlines
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={fetchDeadlines}
            className="p-2.5 rounded-lg bg-[#1F1F1F] hover:bg-[#2F2F2F] text-[#6B6B6B] hover:text-white transition-colors"
            title="Refresh"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-purple-500/20">
              <Calendar size={18} className="text-purple-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{summary.total || 0}</div>
              <div className="text-xs text-[#6B6B6B]">Total Deadlines</div>
            </div>
          </div>
        </div>
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-red-500/20">
              <AlertTriangle size={18} className="text-red-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-red-400">{deadlines.filter(d => d.status_category === 'overdue').length}</div>
              <div className="text-xs text-[#6B6B6B]">Overdue</div>
            </div>
          </div>
        </div>
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-amber-500/20">
              <Flag size={18} className="text-amber-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-amber-400">{summary.urgent || 0}</div>
              <div className="text-xs text-[#6B6B6B]">Urgent (7 days)</div>
            </div>
          </div>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-blue-500/20">
              <Clock size={18} className="text-blue-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-400">{summary.upcoming || 0}</div>
              <div className="text-xs text-[#6B6B6B]">Upcoming (30 days)</div>
            </div>
          </div>
        </div>
      </div>

      {/* View Toggle & Timeline Controls */}
      <div className="flex items-center justify-between bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
        <div className="flex items-center gap-2">
          {['timeline', 'list'].map(mode => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`px-4 py-2 text-sm font-medium rounded-lg capitalize transition-colors ${
                viewMode === mode 
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                  : 'text-[#6B6B6B] hover:text-white hover:bg-[#1F1F1F]'
              }`}
            >
              {mode}
            </button>
          ))}
        </div>

        {viewMode === 'timeline' && (
          <div className="flex items-center gap-2">
            <button 
              onClick={() => shiftTimeline(-14)}
              className="p-2 rounded-lg bg-[#1F1F1F] hover:bg-[#2F2F2F] text-[#6B6B6B] hover:text-white transition-colors"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={goToToday}
              className="px-3 py-2 text-sm font-medium bg-[#1F1F1F] hover:bg-[#2F2F2F] text-[#A0A0A0] hover:text-white rounded-lg transition-colors"
            >
              Today
            </button>
            <button 
              onClick={() => shiftTimeline(14)}
              className="p-2 rounded-lg bg-[#1F1F1F] hover:bg-[#2F2F2F] text-[#6B6B6B] hover:text-white transition-colors"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}
      </div>

      {/* Timeline View */}
      {viewMode === 'timeline' && (
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl overflow-hidden">
          {/* Timeline Header */}
          <div className="flex border-b border-[#1F1F1F] overflow-x-auto">
            {timelineDays.map((day, idx) => {
              const dayDeadlines = getDeadlinesForDay(day);
              const hasOverdue = dayDeadlines.some(d => d.status_category === 'overdue');
              const hasUrgent = dayDeadlines.some(d => d.status_category === 'urgent');
              
              return (
                <div 
                  key={idx}
                  className={`flex-shrink-0 w-20 p-2 text-center border-r border-[#1F1F1F] ${
                    isToday(day) ? 'bg-emerald-500/10' : 
                    isWeekend(day) ? 'bg-[#0A0A0A]' : ''
                  }`}
                >
                  <div className={`text-xs ${isToday(day) ? 'text-emerald-400 font-bold' : 'text-[#6B6B6B]'}`}>
                    {getDayName(day)}
                  </div>
                  <div className={`text-sm font-medium ${isToday(day) ? 'text-emerald-400' : 'text-white'}`}>
                    {formatDate(day)}
                  </div>
                  {dayDeadlines.length > 0 && (
                    <div className={`mt-1 text-xs font-bold ${
                      hasOverdue ? 'text-red-400' : 
                      hasUrgent ? 'text-amber-400' : 'text-blue-400'
                    }`}>
                      {dayDeadlines.length} due
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Timeline Body - Gantt Bars */}
          <div className="relative min-h-[400px] overflow-x-auto">
            {/* Today indicator line */}
            {timelineDays.findIndex(d => isToday(d)) >= 0 && (
              <div 
                className="absolute top-0 bottom-0 w-0.5 bg-emerald-500 z-10"
                style={{ left: `${(timelineDays.findIndex(d => isToday(d)) + 0.5) * 80}px` }}
              />
            )}

            {/* Obligation Gantt Bars */}
            <div className="p-4 space-y-2">
              {deadlines.slice(0, 15).map((deadline, idx) => {
                const dueDate = new Date(deadline.due_date);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                
                const startIdx = timelineDays.findIndex(d => d.toDateString() === today.toDateString());
                const endIdx = timelineDays.findIndex(d => d.toDateString() === dueDate.toDateString());
                
                const barLeft = Math.max(0, Math.min(startIdx, endIdx)) * 80;
                const barWidth = Math.max(80, Math.abs(endIdx - startIdx + 1) * 80);
                
                const isOverdue = deadline.days_until < 0;
                const isUrgent = deadline.days_until <= 7 && deadline.days_until >= 0;
                
                const barColor = isOverdue ? 'bg-red-500/30 border-red-500/50' :
                                isUrgent ? 'bg-amber-500/30 border-amber-500/50' :
                                'bg-blue-500/30 border-blue-500/50';

                return (
                  <div 
                    key={deadline.id || idx}
                    className="relative h-14 group"
                  >
                    {/* Framework/Category Label */}
                    <div 
                      className="absolute left-2 top-1/2 -translate-y-1/2 z-20 max-w-[150px] truncate"
                    >
                      <span className="text-xs text-[#6B6B6B]">
                        {deadline.framework?.replace('_', '/') || 'General'}
                      </span>
                    </div>

                    {/* Gantt Bar */}
                    <div
                      className={`absolute top-1 bottom-1 rounded-lg border cursor-pointer transition-all ${barColor} hover:opacity-90`}
                      style={{ 
                        left: `${barLeft + 160}px`, 
                        width: `${Math.max(barWidth - 160, 80)}px`
                      }}
                      onClick={() => setSelectedObligation(deadline.id)}
                    >
                      <div className="flex items-center h-full px-3 gap-2 overflow-hidden">
                        {/* Progress */}
                        <div className="w-8 h-8 shrink-0 relative">
                          <svg className="w-8 h-8 -rotate-90">
                            <circle cx="16" cy="16" r="12" fill="none" stroke="#1F1F1F" strokeWidth="3" />
                            <circle 
                              cx="16" cy="16" r="12" fill="none" 
                              stroke={isOverdue ? '#ef4444' : isUrgent ? '#f59e0b' : '#3b82f6'} 
                              strokeWidth="3"
                              strokeLinecap="round"
                              strokeDasharray={`${(deadline.progress || 0) * 0.754} 75.4`}
                            />
                          </svg>
                          <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white">
                            {deadline.progress || 0}%
                          </span>
                        </div>

                        {/* Title */}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-white truncate">
                            {deadline.title}
                          </p>
                          <p className="text-[10px] text-[#6B6B6B] truncate">
                            {deadline.owner_name || 'Unassigned'} • {formatDate(dueDate)}
                          </p>
                        </div>

                        {/* Status Badge */}
                        <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded shrink-0 ${
                          isOverdue ? 'bg-red-500/30 text-red-400' :
                          isUrgent ? 'bg-amber-500/30 text-amber-400' :
                          'bg-blue-500/30 text-blue-400'
                        }`}>
                          {deadline.days_until < 0 
                            ? `${Math.abs(deadline.days_until)}d late` 
                            : `${deadline.days_until}d`}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="space-y-4">
          {/* Overdue */}
          {deadlines.filter(d => d.status_category === 'overdue').length > 0 && (
            <DeadlineSection 
              title="Overdue" 
              items={deadlines.filter(d => d.status_category === 'overdue')}
              onSelect={setSelectedObligation}
              color="red"
            />
          )}

          {/* Urgent */}
          {deadlines.filter(d => d.status_category === 'urgent').length > 0 && (
            <DeadlineSection 
              title="Urgent (7 days)" 
              items={deadlines.filter(d => d.status_category === 'urgent')}
              onSelect={setSelectedObligation}
              color="amber"
            />
          )}

          {/* Upcoming */}
          {deadlines.filter(d => d.status_category === 'upcoming').length > 0 && (
            <DeadlineSection 
              title="Upcoming (30 days)" 
              items={deadlines.filter(d => d.status_category === 'upcoming')}
              onSelect={setSelectedObligation}
              color="blue"
            />
          )}

          {/* Planned */}
          {deadlines.filter(d => d.status_category === 'planned').length > 0 && (
            <DeadlineSection 
              title="Planned" 
              items={deadlines.filter(d => d.status_category === 'planned')}
              onSelect={setSelectedObligation}
              color="gray"
            />
          )}
        </div>
      )}

      {/* Detail Drawer */}
      <ObligationDetailDrawerV2
        obligationId={selectedObligation}
        open={!!selectedObligation}
        onClose={() => setSelectedObligation(null)}
        onUpdate={fetchDeadlines}
      />
    </div>
  );
}

// Deadline Section Component
function DeadlineSection({ title, items, onSelect, color }) {
  const colors = {
    red: 'border-red-500/30 bg-red-500/5',
    amber: 'border-amber-500/30 bg-amber-500/5',
    blue: 'border-blue-500/30 bg-blue-500/5',
    gray: 'border-[#1F1F1F] bg-[#0A0A0A]',
  };

  const textColors = {
    red: 'text-red-400',
    amber: 'text-amber-400',
    blue: 'text-blue-400',
    gray: 'text-[#6B6B6B]',
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'No date';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      weekday: 'short', month: 'short', day: 'numeric' 
    });
  };

  return (
    <div className={`border rounded-xl p-4 ${colors[color]}`}>
      <h3 className={`text-sm font-semibold mb-4 ${textColors[color]}`}>
        {title} ({items.length})
      </h3>
      <div className="space-y-2">
        {items.map(item => (
          <div 
            key={item.id}
            onClick={() => onSelect(item.id)}
            className="flex items-center gap-4 p-3 bg-[#111111] border border-[#1F1F1F] rounded-lg cursor-pointer hover:border-[#2F2F2F] transition-colors"
          >
            {/* Progress */}
            <div className="w-10 h-10 shrink-0 relative">
              <svg className="w-10 h-10 -rotate-90">
                <circle cx="20" cy="20" r="16" fill="none" stroke="#1F1F1F" strokeWidth="3" />
                <circle 
                  cx="20" cy="20" r="16" fill="none" 
                  stroke={item.progress === 100 ? '#10b981' : '#3b82f6'} 
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray={`${(item.progress || 0) * 1.005} 100.5`}
                />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white">
                {item.progress || 0}%
              </span>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2 py-0.5 text-[10px] font-mono bg-emerald-500/20 text-emerald-400 rounded">
                  {item.obligation_code}
                </span>
                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${
                  item.priority === 'critical' ? 'bg-red-500/20 text-red-400' :
                  item.priority === 'high' ? 'bg-amber-500/20 text-amber-400' :
                  'bg-blue-500/20 text-blue-400'
                }`}>
                  {item.priority}
                </span>
              </div>
              <p className="text-sm font-medium text-white truncate">{item.title}</p>
              <div className="flex items-center gap-4 mt-1 text-xs text-[#6B6B6B]">
                <span>{item.framework?.replace('_', '/') || 'General'}</span>
                <span>{item.owner_name || 'Unassigned'}</span>
              </div>
            </div>

            {/* Due Date */}
            <div className="text-right shrink-0">
              <p className={`text-sm font-medium ${textColors[color]}`}>
                {formatDate(item.due_date)}
              </p>
              <p className={`text-xs ${textColors[color]}`}>
                {item.days_until < 0 
                  ? `${Math.abs(item.days_until)} days overdue` 
                  : `${item.days_until} days left`}
              </p>
            </div>

            <ArrowRight size={16} className="text-[#4A4A4A] shrink-0" />
          </div>
        ))}
      </div>
    </div>
  );
}
